"use client";

import React, { useState, useRef } from "react";
import { useParallax } from "@/hooks/useParallax";
import styles from "./page.module.scss";
import { toast, Toaster } from "sonner";
import EmailHeader from "@/components/email/Email";

const ContactPage = () => {
	const cassetteRef = useRef<HTMLImageElement>(null);
	const [step, setStep] = useState(1);

	// Apply subtle parallax to cassette image
	useParallax(cassetteRef, {
		speed: 0.7,
		direction: 'down',
		distance: 60,
		scrub: 1.5,
	});
	const [formData, setFormData] = useState({
		projectType: [] as string[],
		budget: "",
		referralSource: [] as string[],
		name: "",
		company: "",
		email: "",
		phone: "",
		duration: "",
		resourceLink: "",
		description: "",
	});
	const [focusedField, setFocusedField] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

	const projectTypes = ["Complete Website", "Web App", "UX & Web Design", "Digital Campaign", "Mobile App", "Branding", "Branding & Web", "Landing Page"];

	const budgetRanges = ["Below $5k", "$5k - $15k", "$20k - $30k", "$35k - $50k", "Get a Quote"];

	const referralSources = ["Site Directory", "Awards Websites", "A Project We Did", "An Article", "A Friend", "Word On The Street"];

	const handleProjectTypeToggle = (type: string) => {
		setFormData((prev) => ({
			...prev,
			projectType: prev.projectType.includes(type) ? prev.projectType.filter((t) => t !== type) : [...prev.projectType, type],
		}));
	};

	const handleBudgetSelect = (budget: string) => {
		setFormData((prev) => ({ ...prev, budget }));
	};

	const handleReferralToggle = (source: string) => {
		setFormData((prev) => ({
			...prev,
			referralSource: prev.referralSource.includes(source) ? prev.referralSource.filter((s) => s !== source) : [...prev.referralSource, source],
		}));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleProceedToStep2 = () => {
		if (formData.projectType.length === 0 || !formData.budget || formData.referralSource.length === 0) {
			toast.error("Please complete all selections before proceeding.");
			return;
		}
		setStep(2);
	};

	const handleSubmit = async () => {
		if (!formData.name || !formData.email) {
			toast.error("Please fill in all required fields.");
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus("idle");

		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(formData),
			});

			if (response.ok) {
				setSubmitStatus("success");
				setFormData({
					projectType: [],
					budget: "",
					referralSource: [],
					name: "",
					company: "",
					email: "",
					phone: "",
					duration: "",
					resourceLink: "",
					description: "",
				});
				setStep(1);
				toast.success("Thank you! Your message has been sent successfully.");
			} else {
				setSubmitStatus("error");
				toast.error("Something went wrong. Please try again.");
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			setSubmitStatus("error");
			toast.error("Something went wrong. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Toaster
				position="top-center"
				toastOptions={{
					style: {
						color: "#000000",
					},
					classNames: {
						success: "success-toast",
						error: "error-toast",
					},
				}}
			/>
			<section className={styles.contactPage}>
				<div className={styles.container}>
					<div className={styles.imageSection}>
						<img ref={cassetteRef} src="/contact/cassette.webp" alt="Cassette tape" className={styles.cassetteImage} />
					</div>

					<div className={`${styles.formSection} ${step === 2 ? styles.step2 : ""}`}>
						{step === 1 ? (
							<>
								<h1 className={styles.title}>
									WANT TO WORK WITH OUR TEAM? WHO WOULDN&apos;T?
									<br />
									<br />
									WHAT KIND OF DREAM PROJECT CAN WE MAKE A REALITY?
								</h1>

								<div className={styles.question}>
									<div className={styles.options}>
										{projectTypes.map((type) => (
											<button key={type} className={`${styles.option} ${formData.projectType.includes(type) ? styles.selected : ""}`} onClick={() => handleProjectTypeToggle(type)} type="button">
												{type}
											</button>
										))}
									</div>
								</div>

								<div className={styles.question}>
									<h2 className={styles.questionTitle}>What&apos;s your budget range</h2>
									<div className={styles.options}>
										{budgetRanges.map((budget) => (
											<button key={budget} className={`${styles.option} ${formData.budget === budget ? styles.selected : ""}`} onClick={() => handleBudgetSelect(budget)} type="button">
												{budget}
											</button>
										))}
									</div>
								</div>

								<div className={styles.question}>
									<h2 className={styles.questionTitle}>So who can we give props to for telling you about us</h2>
									<div className={styles.options}>
										{referralSources.map((source) => (
											<button key={source} className={`${styles.option} ${formData.referralSource.includes(source) ? styles.selected : ""}`} onClick={() => handleReferralToggle(source)} type="button">
												{source}
											</button>
										))}
									</div>
								</div>

								<button className={styles.proceedButton} onClick={handleProceedToStep2} type="button">
									Proceed
								</button>
							</>
						) : (
							<>
								<h1 className={styles.title}>
									PLEASE COMPLETE THIS PROJECT QUESTIONNAIRE.
									<br />
									<br />
									IT&apos;S SIMILAR TO A BUZZFEED QUIZ—PEOPLE STILL ENJOY THOSE, RIGHT?
								</h1>
								{/* <p className={styles.subtitle}></p> */}

								<div className={styles.detailedForm}>
									<div className={styles.formRow}>
										<div className={`${styles.formGroup} ${focusedField === "name" || formData.name ? styles.focused : ""}`}>
											<input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField(null)} className={styles.input} required />
											<label htmlFor="name" className={styles.label}>
												Your name*
											</label>
										</div>
										<div className={`${styles.formGroup} ${focusedField === "company" || formData.company ? styles.focused : ""}`}>
											<input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} onFocus={() => setFocusedField("company")} onBlur={() => setFocusedField(null)} className={styles.input} required />
											<label htmlFor="company" className={styles.label}>
												Your Company*
											</label>
										</div>
									</div>

									<div className={styles.formRow}>
										<div className={`${styles.formGroup} ${focusedField === "email" || formData.email ? styles.focused : ""}`}>
											<input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} className={styles.input} required />
											<label htmlFor="email" className={styles.label}>
												Email*
											</label>
										</div>
										<div className={`${styles.formGroup} ${focusedField === "phone" || formData.phone ? styles.focused : ""}`}>
											<input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} onFocus={() => setFocusedField("phone")} onBlur={() => setFocusedField(null)} className={styles.input} />
											<label htmlFor="phone" className={styles.label}>
												Phone (optional)
											</label>
										</div>
									</div>

									<div className={styles.formRow}>
										<div className={`${styles.formGroup} ${focusedField === "duration" || formData.duration ? styles.focused : ""}`}>
											<input type="text" id="duration" name="duration" value={formData.duration} onChange={handleInputChange} onFocus={() => setFocusedField("duration")} onBlur={() => setFocusedField(null)} className={styles.input} />
											<label htmlFor="duration" className={styles.label}>
												Duration
											</label>
										</div>
										<div className={`${styles.formGroup} ${focusedField === "resourceLink" || formData.resourceLink ? styles.focused : ""}`}>
											<input type="text" id="resourceLink" name="resourceLink" value={formData.resourceLink} onChange={handleInputChange} onFocus={() => setFocusedField("resourceLink")} onBlur={() => setFocusedField(null)} className={styles.input} />
											<label htmlFor="resourceLink" className={styles.label}>
												Link to additional resources
											</label>
										</div>
									</div>

									<div className={`${styles.formGroup} ${focusedField === "description" || formData.description ? styles.focused : ""}`}>
										<textarea id="description" name="description" value={formData.description} onChange={handleInputChange} onFocus={() => setFocusedField("description")} onBlur={() => setFocusedField(null)} className={styles.textarea} rows={6} />
										<label htmlFor="description" className={styles.label}>
											Describe your project
										</label>
									</div>

									<button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting} type="button">
										{isSubmitting ? "Sending..." : "Submit"}
									</button>
								</div>
							</>
						)}
					</div>
				</div>
				<EmailHeader />
			</section>
		</>
	);
};

export default ContactPage;
